import { Center, CircularProgress } from '@chakra-ui/react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useProductsV2 } from '@partiu/api/dashboard/hooks.ts';
import { AgreementModules, deriveModules, EMPTY_MODULES } from '@partiu/api/dashboard/modules.ts';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';

const ModulesContext = createContext<AgreementModules>(EMPTY_MODULES);

/**
 * Carrega os produtos do convênio e deriva quais módulos estão ativos.
 *
 * Segura o render dos filhos até o id do convênio ser conhecido e os produtos
 * chegarem: sem isso o menu monta com as flags zeradas e os itens piscam quando
 * a resposta chega. Pior, em carregamento a frio (deep-link ou F5) o id do
 * ThemeProvider começa `null` até resolver via rede — com a query desabilitada
 * nesse instante, o react-query reporta `idle` (não `loading`), e sem essa
 * guarda os filhos renderizariam com todos os módulos falsos, derrubando
 * usuários com direito de acesso de volta para /dashboard. Se a busca falhar,
 * seguimos com todos os módulos desligados — nunca voltamos para o agreement.
 */
export function ProductsProvider({ children }: PropsWithChildren) {
  const { id: agreementId } = useTheme();

  const { data, isLoading } = useProductsV2(
    { page_size: 300, include_inactive: true, agreement: agreementId },
    { enabled: !!agreementId, refetchOnMount: false }
  );

  const modules = useMemo(() => deriveModules(data?.results), [data]);

  if (!agreementId || isLoading) {
    return (
      <Center width="100%" height="100vh">
        <CircularProgress isIndeterminate color="black" />
      </Center>
    );
  }

  return <ModulesContext.Provider value={modules}>{children}</ModulesContext.Provider>;
}

export function useModules(): AgreementModules {
  return useContext(ModulesContext);
}
